/**
 * API service for resumable worksheet upload: session create/resume, upload page, upload PDF, clear pages, finalize.
 * All endpoints are relative to api baseURL.
 */
import api from './api';
import {
  WORKSHEET_SESSION_CREATE,
  WORKSHEET_SESSION_ACTIVE,
  WORKSHEET_SESSION_UPLOAD_PAGE,
  WORKSHEET_SESSION_CLEAR_PAGES,
  WORKSHEET_SESSION_UPLOAD_PDF,
  WORKSHEET_SESSION_FINALIZE,
} from '../config/Config';

/**
 * Create or resume active session.
 * @param {{ test_attempt?: string|number, mode: 'pdf'|'scan' }} payload
 * @returns {Promise<{ data: object }>}
 */
export function createOrResumeSession(payload) {
  return api.post(WORKSHEET_SESSION_CREATE, payload);
}

/**
 * Get existing active session, scoped by test (and optionally by attempt).
 * @param {{ test_attempt?: string|number, test_id?: string|number }} params
 * @returns {Promise<{ data: object }>}
 */
export function getActiveSession(params = {}) {
  const query = new URLSearchParams();
  if (params.test_attempt != null && params.test_attempt !== '') query.set('test_attempt', params.test_attempt);
  if (params.test_id != null && params.test_id !== '') query.set('test_id', params.test_id);
  const url = query.toString() ? `${WORKSHEET_SESSION_ACTIVE}?${query}` : WORKSHEET_SESSION_ACTIVE;
  return api.get(url);
}

/**
 * Upload one page image (scan mode). Instant upload per page.
 * @param {string} sessionId - UUID
 * @param {File} imageFile
 * @returns {Promise<{ data: { id: number, page_number: number, image: string, uploaded_at: string } }>}
 */
export function uploadPage(sessionId, imageFile) {
  const form = new FormData();
  form.append('image', imageFile);
  return api.post(WORKSHEET_SESSION_UPLOAD_PAGE(sessionId), form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/**
 * Clear all scanned pages (e.g. when switching to PDF).
 * @param {string} sessionId - UUID
 */
export function clearPages(sessionId) {
  return api.delete(WORKSHEET_SESSION_CLEAR_PAGES(sessionId));
}

/**
 * Upload single PDF and set mode to pdf (clears pages on server).
 * @param {string} sessionId - UUID
 * @param {File} pdfFile
 */
export function uploadPdf(sessionId, pdfFile) {
  const form = new FormData();
  form.append('pdf', pdfFile);
  return api.post(WORKSHEET_SESSION_UPLOAD_PDF(sessionId), form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/**
 * Finalize session: merge scan pages to PDF or keep uploaded PDF; returns final_pdf_url.
 * @param {string} sessionId - UUID
 * @returns {Promise<{ data: { session: object, final_pdf_url: string } }>}
 */
export function finalizeSession(sessionId) {
  return api.post(WORKSHEET_SESSION_FINALIZE(sessionId));
}
